import React, { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { nav, brand, footer as footerData, languages } from "@/content/site";

function Header() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("hubinall_lang") || "EN");
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  const changeLang = (c) => {
    setLang(c);
    localStorage.setItem("hubinall_lang", c);
  };

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-[var(--bone)]/85 border-b border-[var(--rule)]"
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-10 lg:px-12 h-16">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
          <Logo size={28} color="var(--ink)" stroke={2} />
          <span className="font-display text-lg tracking-tight text-[var(--ink)]">
            Hubinall
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7" data-testid="nav-primary">
          {nav.slice(1).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                "text-[13px] tracking-tight transition-colors " +
                (isActive
                  ? "text-[var(--ochre)]"
                  : "text-[var(--ink)] hover:text-[var(--ochre)]")
              }
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <div
            className="flex items-center border border-[var(--rule)] divide-x divide-[var(--rule)]"
            data-testid="lang-switcher"
          >
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => changeLang(l.code)}
                className={
                  "px-2.5 py-1 font-mono text-[10px] tracking-widest transition-colors " +
                  (lang === l.code
                    ? "bg-[var(--ink)] text-[var(--bone)]"
                    : "text-[var(--ink)] hover:bg-[var(--bone-2)]")
                }
                data-testid={`lang-${l.code}`}
                title={l.label}
              >
                {l.code}
              </button>
            ))}
          </div>
          <Link
            to="/contact?form=call"
            className="font-mono text-[11px] uppercase tracking-[0.2em] bg-[var(--ink)] text-[var(--bone)] px-4 py-2.5 hover:bg-[var(--ochre)] transition-colors"
            data-testid="nav-cta-request-call"
          >
            Request a Call
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden w-10 h-10 flex items-center justify-center border border-[var(--rule)]"
          aria-label="Menu"
          data-testid="nav-mobile-toggle"
        >
          <span className="flex flex-col gap-1.5">
            <span className="block w-4 h-px bg-[var(--ink)]" />
            <span className="block w-4 h-px bg-[var(--ink)]" />
            <span className="block w-4 h-px bg-[var(--ink)]" />
          </span>
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[var(--rule)] bg-[var(--bone)]" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  "py-3 border-b border-[var(--rule)] text-base " +
                  (isActive ? "text-[var(--ochre)]" : "text-[var(--ink)]")
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-4 flex gap-3">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => changeLang(l.code)}
                  className={
                    "px-3 py-2 font-mono text-[11px] tracking-widest border " +
                    (lang === l.code
                      ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]"
                      : "border-[var(--rule)] text-[var(--ink)]")
                  }
                >
                  {l.code}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[var(--ink)] text-[var(--bone)] border-t border-[var(--ink-2)]" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 border-b border-[var(--ink-2)] pb-16">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <Logo size={32} color="var(--ochre)" stroke={2} />
              <span className="font-display text-lg tracking-tight">Hubinall</span>
            </div>
            <p className="text-sm leading-relaxed text-[var(--bone)]/70 max-w-sm">
              {footerData.brand}
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ochre)] mt-6">
              {footerData.brandNote}
            </p>
          </div>

          {footerData.columns.map((col, ci) => (
            <div key={col.title} className="lg:col-span-2 lg:col-start-auto">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--bone)]/50 mb-5">
                0{ci + 1} · {col.title}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-sm text-[var(--bone)]/80 hover:text-[var(--ochre)] transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--bone)]/50 mb-5">
              04 · Contact
            </div>
            <ul className="space-y-3 text-sm text-[var(--bone)]/80">
              <li>
                <a href="mailto:hi@hubinall.com" className="hover:text-[var(--ochre)]">
                  hi@hubinall.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center">
          <div className="flex flex-wrap gap-x-5 gap-y-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--bone)]/55">
            {footerData.legalLinks.map((l) =>
              l.to.startsWith("#") ? (
                <button
                  key={l.label}
                  onClick={() => {
                    localStorage.removeItem("hubinall_cookie_prefs");
                    window.location.reload();
                  }}
                  className="hover:text-[var(--ochre)] transition-colors"
                  data-testid="footer-manage-cookies"
                >
                  {l.label}
                </button>
              ) : (
                <Link key={l.to} to={l.to} className="hover:text-[var(--ochre)] transition-colors">
                  {l.label}
                </Link>
              ),
            )}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone)]/50">
            {footerData.copyright}
          </div>
        </div>
      </div>
    </footer>
  );
}

function CookieBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const v = localStorage.getItem("hubinall_cookie_prefs");
    if (!v) setShow(true);
  }, []);
  const choose = (val) => {
    localStorage.setItem("hubinall_cookie_prefs", val);
    setShow(false);
  };
  if (!show) return null;
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--ink)] text-[var(--bone)] border-t border-[var(--ochre)]"
      data-testid="cookie-banner"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 py-5 flex flex-col lg:flex-row gap-4 items-start lg:items-center">
        <p className="text-sm text-[var(--bone)]/85 leading-relaxed lg:flex-1">
          We use cookies to improve site functionality, analyse traffic, and understand how
          visitors use Hubinall. You can accept all cookies, reject non-essential cookies, or
          manage your preferences.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => choose("all")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] bg-[var(--ochre)] text-[var(--bone)] px-4 py-2.5 hover:bg-[var(--ochre-hover)]"
            data-testid="cookie-accept-all"
          >
            Accept All
          </button>
          <button
            onClick={() => choose("essential")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] border border-[var(--bone)]/40 text-[var(--bone)] px-4 py-2.5 hover:bg-[var(--bone)] hover:text-[var(--ink)]"
            data-testid="cookie-reject"
          >
            Reject Non-Essential
          </button>
          <button
            onClick={() => choose("manage")}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--bone)]/70 px-4 py-2.5 hover:text-[var(--ochre)]"
            data-testid="cookie-manage"
          >
            Manage Preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen bg-[var(--bone)] text-[var(--ink)] flex flex-col">
      <Header />
      <main className="flex-1" data-testid="main-content">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
